import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Users, TrendingUp } from "lucide-react";
import { CASES } from "@/data/index";
import { springPresets } from "@/lib/motion";

const CASE_IMAGES: Record<string, string> = {
  "manufacturing": "/images/gen/case_leadership_2.png",
  "it": "/images/gen/case_orgteam_2.png",
  "retail": "/images/gen/case_ai_seminar_2.png",
};

/* @section: cases-hero */
function CasesHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">Case Studies</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            実績・導入事例
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground text-lg leading-[1.9]">
            これまでに支援した企業・組織の一部をご紹介します。
            掲載にあたり、企業名・個人情報は匿名化しています。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* @section: cases-list */
function CasesList() {
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="flex flex-col gap-16">
          {CASES.map((c, i) => {
            const imgSrc = CASE_IMAGES[c.id];
            const isEven = i % 2 === 0;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springPresets.gentle, delay: i * 0.08 }}
                className="border border-border rounded-sm overflow-hidden"
              >
                {/* Image banner */}
                {imgSrc && (
                  <div className="relative w-full h-56 md:h-72 overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={c.industry}
                      className="w-full h-full object-cover"
                    />
                    {/* overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    {/* tag badge on image */}
                    <div className="absolute bottom-4 left-6 flex items-center gap-3">
                      <span className="px-3 py-1 bg-primary text-primary-foreground text-xs tracking-wider rounded-sm">
                        {c.tag}
                      </span>
                      <span className="flex items-center gap-1.5 text-white/90 text-xs">
                        <Users size={12} />
                        {c.scale}
                      </span>
                    </div>
                  </div>
                )}

                {/* Header */}
                <div className="bg-muted/30 px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border">
                  <div className="flex items-center gap-4">
                    <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-primary" />
                    </div>
                    <h3
                      className="text-xl font-semibold text-foreground"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {c.industry}
                    </h3>
                  </div>
                </div>

                {/* Body */}
                <div className="px-8 py-8 grid md:grid-cols-3 gap-8">
                  <div>
                    <div className="text-xs text-primary tracking-wider mb-3 uppercase">課題</div>
                    <p className="text-sm text-muted-foreground leading-[1.9]">{c.challenge}</p>
                  </div>
                  <div>
                    <div className="text-xs text-primary tracking-wider mb-3 uppercase">アプローチ</div>
                    <p className="text-sm text-muted-foreground leading-[1.9]">{c.approach}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp size={13} className="text-primary" />
                      <div className="text-xs text-primary tracking-wider uppercase">成果・変化</div>
                    </div>
                    <p className="text-sm text-foreground leading-[1.9] font-medium">{c.result}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* @section: cases-cta */
function CasesCTA() {
  return (
    <section className="py-20 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <h2
            className="text-2xl md:text-3xl font-semibold text-foreground mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            貴社の課題もご相談ください。
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
            業種・規模に関わらず、まずはヒアリングから。初回相談は無料です。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
            style={{ letterSpacing: "0.1em" }}
          >
            無料相談を申し込む
            <ArrowRight size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Cases() {
  return (
    <>
      <CasesHero />
      <CasesList />
      <CasesCTA />
    </>
  );
}
